<div class="geoserver-page">
  <div class="page-header page-header--no-search">
    <div class="page-header__title">
      <h2>{{ 'GEOSERVER_MONITOR_TITLE' | translate }}</h2>
      <p>{{ 'GEOSERVER_MONITOR_SUBTITLE' | translate }}</p>
    </div>
    <div class="header-actions">
      <app-secondary-button
        class="header-actions__refresh"
        title="Refresh"
        icon="refresh"
        [disable]="loading()"
        (action)="load()"
      ></app-secondary-button>
    </div>
  </div>

  @if (errorMessage()) {
    <div class="error-message" role="alert">{{ errorMessage() | translate }}</div>
  }

  @if (loading()) {
    <app-loading [loading]="true"></app-loading>
  } @else {
    @if (snapshot(); as geo) {
    <div class="facts">
      <div class="fact fact--wide">
        <span class="fact__label">{{ 'GEOSERVER_BASE_URL' | translate }}</span>
        <app-copyable-identifier [value]="geo.baseUrl || ''"></app-copyable-identifier>
      </div>
      <div class="fact">
        <span class="fact__label">{{ 'GEOSERVER_REACHABLE' | translate }}</span>
        <span>{{ reachableLabel() | translate }}</span>
      </div>
      <div class="fact">
        <span class="fact__label">{{ 'GEOSERVER_VERSION' | translate }}</span>
        <span>{{ geo.version || '—' }}</span>
      </div>
      <div class="fact">
        <span class="fact__label">{{ 'GEOSERVER_WORKSPACES' | translate }}</span>
        <span>{{ geo.workspaceCount ?? '—' }}</span>
      </div>
      <div class="fact">
        <span class="fact__label">{{ 'GEOSERVER_LAYERS' | translate }}</span>
        <span>{{ geo.layerCount ?? '—' }}</span>
      </div>
      <div class="fact">
        <span class="fact__label">{{ 'GEOSERVER_COVERAGE_STORES' | translate }}</span>
        <span>{{ geo.coverageStoreCount ?? '—' }}</span>
      </div>
      <div class="fact">
        <span class="fact__label">{{ 'GEOSERVER_DATA_STORES' | translate }}</span>
        <span>{{ geo.dataStoreCount ?? '—' }}</span>
      </div>
      <div class="fact">
        <span class="fact__label">{{ 'GEOSERVER_STYLES' | translate }}</span>
        <span>{{ geo.styleCount ?? '—' }}</span>
      </div>
      <div class="fact">
        <span class="fact__label">{{ 'GEOSERVER_LAYER_GROUPS' | translate }}</span>
        <span>{{ geo.layerGroupCount ?? '—' }}</span>
      </div>
      <div class="fact">
        <span class="fact__label">{{ 'GEOSERVER_GWC' | translate }}</span>
        <span>{{ geo.gwcLayerCount ?? '—' }}</span>
      </div>
      <div class="fact">
        <span class="fact__label">{{ 'GEOSERVER_GLOBAL_SERVICES' | translate }}</span>
        <span>{{ geo.globalServices == null ? '—' : (geo.globalServices ? 'Yes' : 'No') | translate }}</span>
      </div>
      <div class="fact">
        <span class="fact__label">{{ 'GEOSERVER_LOG_LEVEL' | translate }}</span>
        <span>{{ geo.logLevel || '—' }}</span>
      </div>
      <div class="fact">
        <span class="fact__label">{{ 'GEOSERVER_CAPABILITIES' | translate }} WMS</span>
        <span>{{ geo.wmsCapabilities?.status || '—' }} · {{ geo.wmsCapabilities?.layerCount ?? '—' }}</span>
      </div>
      <div class="fact">
        <span class="fact__label">{{ 'GEOSERVER_CAPABILITIES' | translate }} WMTS</span>
        <span>{{ geo.wmtsCapabilities?.status || '—' }} · {{ geo.wmtsCapabilities?.layerCount ?? '—' }}</span>
      </div>
      @if (geo.proxyBaseUrl) {
        <div class="fact fact--wide">
          <span class="fact__label">Proxy base URL</span>
          <app-copyable-identifier [value]="geo.proxyBaseUrl"></app-copyable-identifier>
        </div>
      }
      @if (geo.wmsCapabilities?.error) {
        <div class="fact fact--wide">
          <span class="fact__label">WMS</span>
          <span>{{ geo.wmsCapabilities?.error }}</span>
        </div>
      }
      @if (geo.wmtsCapabilities?.error) {
        <div class="fact fact--wide">
          <span class="fact__label">WMTS</span>
          <span>{{ geo.wmtsCapabilities?.error }}</span>
        </div>
      }
      <div class="fact">
        <span class="fact__label">{{ 'GEOSERVER_TILE_PROBE' | translate }}</span>
        <span>{{ tileStatus() }}</span>
      </div>
      @if (geo.error) {
        <div class="fact fact--wide">
          <span class="fact__label">{{ 'Error' | translate }}</span>
          <span>{{ geo.error }}</span>
        </div>
      }
      @if (tileError()) {
        <div class="fact fact--wide">
          <span class="fact__label">{{ 'GEOSERVER_TILE_PROBE' | translate }}</span>
          <span>{{ tileError() }}</span>
        </div>
      }
    </div>

    <div class="orch-table-panel">
      <h3>{{ 'GEOSERVER_WORKSPACES' | translate }}</h3>
      <app-data-table
        [columns]="columns"
        [data]="workspaces()"
        [config]="tableConfig()"
        [totalRecords]="workspaceRows()"
      ></app-data-table>
    </div>
    <div class="orch-table-panel">
      <h3>{{ 'GEOSERVER_TILE_PROBES' | translate }}</h3>
      <app-data-table
        [columns]="probeColumns"
        [data]="tileProbes()"
        [config]="tableConfig()"
        [totalRecords]="probeRows()"
      ></app-data-table>
    </div>
    <div class="orch-table-panel">
      <h3>{{ 'GEOSERVER_SERVICES' | translate }}</h3>
      <app-data-table
        [columns]="serviceColumns"
        [data]="services()"
        [config]="tableConfig()"
        [totalRecords]="serviceRows()"
      ></app-data-table>
    </div>
    <div class="orch-table-panel">
      <h3>{{ 'GEOSERVER_STORES' | translate }}</h3>
      <app-data-table
        [columns]="storeColumns"
        [data]="stores()"
        [config]="tableConfig()"
        [totalRecords]="storeRows()"
      ></app-data-table>
    </div>
    <div class="orch-table-panel">
      <h3>{{ 'GEOSERVER_MODULES' | translate }}</h3>
      <app-data-table
        [columns]="moduleColumns"
        [data]="modules()"
        [config]="tableConfig()"
        [totalRecords]="moduleRows()"
      ></app-data-table>
    </div>
    <div class="orch-table-panel">
      <h3>{{ 'GEOSERVER_SYSTEM_STATUS' | translate }}</h3>
      <app-data-table
        [columns]="metricColumns"
        [data]="systemStatus()"
        [config]="tableConfig()"
        [totalRecords]="metricRows()"
      ></app-data-table>
    </div>
    }
  }
</div>
